import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../lib/api';
import { formatAUD, formatPct, formatDateAU } from '../../../shared/format';
import { QAEmpty } from './QAEmpty';
import { QASkeleton } from './QASkeleton';
import { QAChartTooltip } from './QAChartTooltip';
import type { PerformanceResponse, CgtResponse, IncomeResponse, PortfolioSnapshot } from '../../../shared/api';

const PERIODS: Array<[string, string]> = [
  ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['1y', '1Y'], ['3y', '3Y'],
  ['5y', '5Y'], ['fytd', 'FYTD'], ['cytd', 'CYTD'], ['itd', 'ITD'],
];

function pct(v: number | null | undefined): string {
  return v == null ? '—' : formatPct(v, 2);
}

export function PerformanceTab({ clientId, portfolio }: { clientId: string; portfolio: PortfolioSnapshot }) {
  const qc = useQueryClient();
  const [selection, setSelection] = useState<string>('consolidated'); // 'consolidated' | accountId
  const [period, setPeriod] = useState<string>('itd');

  const base = selection === 'consolidated'
    ? `/api/clients/${clientId}`
    : `/api/clients/${clientId}/accounts/${selection}`;

  const perfQ = useQuery({ queryKey: ['perf', clientId, selection], queryFn: () => api<{ data: PerformanceResponse }>(`${base}/performance`) });
  const cgtQ = useQuery({ queryKey: ['perf-cgt', clientId, selection], queryFn: () => api<{ data: CgtResponse }>(`${base}/cgt`) });
  const incomeQ = useQuery({ queryKey: ['perf-income', clientId, selection], queryFn: () => api<{ data: IncomeResponse }>(`${base}/income`) });

  const refresh = useMutation({
    mutationFn: () => api('/api/integrations/market/refresh', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  });

  const perf = perfQ.data?.data;

  // Indexed-to-100 chart: portfolio vs benchmark, sliced to the selected period.
  const chartData = useMemo(() => {
    if (!perf) return [];
    const start = perf.periods[period]?.startDate ?? null;
    const series = perf.series.filter((p) => !start || p.date >= start);
    if (series.length === 0) return [];
    const p0 = series[0].valueCents || 1;
    const bm = new Map((perf.benchmark?.series ?? []).map((b) => [b.date, b.valueCents]));
    const bmStart = perf.benchmark?.series.find((b) => !start || b.date >= start)?.valueCents ?? null;
    return series.map((p) => {
      const bmv = bm.get(p.date);
      return {
        date: p.date,
        portfolio: Math.round((p.valueCents / p0) * 1000) / 10,
        benchmark: bmv != null && bmStart ? Math.round((bmv / bmStart) * 1000) / 10 : null,
      };
    });
  }, [perf, period]);

  if (perfQ.isLoading) return <QASkeleton variant="cards" />;
  if (perfQ.isError || !perf) return <div className="qa-badge qa-badge--out mt-6">Unable to load performance</div>;

  const pr = perf.periods[period];
  const bmTwr = perf.benchmark?.periods[period]?.twr ?? null;
  const excess = pr?.twr != null && bmTwr != null ? pr.twr - bmTwr : null;

  return (
    <div className="mt-6 space-y-8">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <select
          className="qa-input text-sm"
          value={selection}
          onChange={(e) => setSelection(e.target.value)}
          aria-label="Portfolio"
        >
          <option value="consolidated">Consolidated household</option>
          {portfolio.accounts.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
        <button type="button" className="qa-btn qa-btn--ghost text-sm" disabled={refresh.isPending} onClick={() => refresh.mutate()}>
          {refresh.isPending ? 'Refreshing…' : 'Refresh prices'}
        </button>
      </div>

      {/* Period pills */}
      <div className="flex flex-wrap gap-2">
        {PERIODS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setPeriod(k)}
            className={`px-2.5 py-1 text-[11px] uppercase font-bold tracking-widest border ${period === k ? 'border-rust text-rust' : 'border-hairline text-ink-mute hover:text-ink'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Return figures */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Return (TWR)" value={pct(pr?.twr)} note={pr?.days ? `${pr.days} days` : ''} />
        <Stat label="Return (MWR/IRR)" value={pct(pr?.mwr)} />
        <Stat label="Benchmark (TWR)" value={pct(bmTwr)} note={perf.benchmark?.name ?? ''} />
        <Stat label="Excess vs benchmark" value={pct(excess)} tone={excess == null ? undefined : excess >= 0 ? 'pos' : 'neg'} />
      </div>

      {/* Chart */}
      <div className="qa-card p-6">
        <p className="qa-eyebrow mb-3">Growth of $100 — portfolio vs benchmark</p>
        {chartData.length < 2 ? (
          <QAEmpty inline title="Not enough valuation history yet.">The nightly valuation job builds this series over time.</QAEmpty>
        ) : (
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="rgba(26,22,20,0.08)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => formatDateAU(String(d))} minTickGap={40} />
                <YAxis tick={{ fontSize: 11 }} width={40} />
                <Tooltip content={<QAChartTooltip valueFormat={(v) => (typeof v === 'number' ? v.toFixed(1) : String(v))} />} />
                <Legend />
                <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke="#B8431A" dot={false} strokeWidth={2} connectNulls />
                <Line type="monotone" dataKey="benchmark" name="Benchmark" stroke="#A07A3F" dot={false} strokeWidth={1.5} strokeDasharray="4 3" connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* CGT + income */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Capital gains</p>
          {cgtQ.isLoading ? <QASkeleton variant="cards" /> : cgtQ.data ? (
            <CgtPanel cgt={cgtQ.data.data} />
          ) : <p className="text-ink-mute text-sm">Unavailable.</p>}
        </div>
        <div className="qa-card p-6">
          <p className="qa-eyebrow mb-3">Income &amp; franking</p>
          {incomeQ.isLoading ? <QASkeleton variant="cards" /> : incomeQ.data ? (
            <IncomePanel income={incomeQ.data.data} />
          ) : <p className="text-ink-mute text-sm">Unavailable.</p>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, note, tone }: { label: string; value: string; note?: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="qa-card p-4">
      <p className="qa-eyebrow">{label}</p>
      <p className={`text-2xl font-display mt-1 ${colour}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</p>
      {note ? <p className="text-[11px] text-ink-mute mt-0.5">{note}</p> : null}
    </div>
  );
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'pos' ? 'text-forest' : tone === 'neg' ? 'text-rust' : 'text-ink';
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-hairline last:border-0">
      <span className="text-sm text-ink-mute">{label}</span>
      <span className={`text-sm font-mono ${colour}`}>{value}</span>
    </div>
  );
}

function CgtPanel({ cgt }: { cgt: CgtResponse }) {
  const u = cgt.unrealised.summary;
  const r = cgt.realised.summary;
  return (
    <div>
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mb-1">Unrealised</p>
      <Row label="Market value" value={formatAUD(u.marketCents / 100)} />
      <Row label="Cost base" value={formatAUD(u.costCents / 100)} />
      <Row label="Gain / loss" value={formatAUD(u.gainCents / 100)} tone={u.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Discount-eligible gain" value={formatAUD(u.discountEligibleGainCents / 100)} />
      <p className="text-[11px] uppercase tracking-widest text-ink-mute mt-4 mb-1">Realised</p>
      <Row label="Net gain / loss" value={formatAUD(r.gainCents / 100)} tone={r.gainCents >= 0 ? 'pos' : 'neg'} />
      <Row label="Discounted gains" value={formatAUD(r.discountedGainCents / 100)} />
      <Row label="Other gains" value={formatAUD(r.otherGainCents / 100)} />
      <Row label="Losses" value={formatAUD(r.lossCents / 100)} tone={r.lossCents < 0 ? 'neg' : undefined} />
    </div>
  );
}

function IncomePanel({ income }: { income: IncomeResponse }) {
  const s = income.summary;
  return (
    <div>
      <Row label="Cash income" value={formatAUD(s.cashAudCents / 100)} />
      <Row label="Franking credits" value={formatAUD(s.frankingCreditsAudCents / 100)} />
      <Row label="Grossed-up total" value={formatAUD(s.grossedUpAudCents / 100)} tone="pos" />
      <Row label="Events" value={String(s.count)} />
      {income.events.length > 0 && (
        <ul className="mt-3 text-xs text-ink-mute space-y-1">
          {income.events.slice(0, 6).map((e, i) => (
            <li key={i} className="flex justify-between">
              <span>{e.payDate ? formatDateAU(e.payDate) : '—'} · {e.securityCode ?? e.incomeType}</span>
              <span className="font-mono">{formatAUD(e.cashAudCents / 100)}{e.frankingCreditsAudCents > 0 ? ` (+${formatAUD(e.frankingCreditsAudCents / 100)} fr)` : ''}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
